import React from 'react';
import {
    BooleanField,
    BooleanInput,
    Create,
    CreateButton,
    Datagrid,
    Edit,
    EditButton,
    ExportButton,
    List,
    regex,
    required,
    SelectInput,
    SimpleForm,
    TextField,
    TextInput,
    TopToolbar,
    useListContext,
    FunctionField,
} from 'react-admin';
// Import new MUI components for layout
import { Grid, Typography, Box } from "@mui/material";
import TestimonialPlaceholderImage from '../../assets/images/testimonial-contestant-placeholder.svg';

const validateCssColor = [
    regex(
        /^(#([A-Fa-f0-9]{3,4}){1,2}|(rgb|hsl)a?\(\s*\d+\s*,\s*\d+%?\s*,\s*\d+%?\s*(,\s*[0-1]?\.?\d+)?\)|transparent|aliceblue|antiquewhite|aqua|aquamarine|azure|beige|bisque|black|blanchedalmond|blue|blueviolet|brown|burlywood|cadetblue|chartreuse|chocolate|coral|cornflowerblue|cornsilk|crimson|cyan|darkblue|darkcyan|darkgoldenrod|darkgray|darkgreen|darkgrey|darkkhaki|darkmagenta|darkolivegreen|darkorange|darkorchid|darkred|darksalmon|darkseagreen|darkslateblue|darkslategray|darkslategrey|darkturquoise|darkviolet|deeppink|deepskyblue|dimgray|dimgrey|dodgerblue|firebrick|floralwhite|forestgreen|fuchsia|gainsboro|ghostwhite|gold|goldenrod|gray|green|greenyellow|grey|honeydew|hotpink|indianred|indigo|ivory|khaki|lavender|lavenderblush|lawngreen|lemonchiffon|lightblue|lightcoral|lightcyan|lightgoldenrodyellow|lightgray|lightgreen|lightgrey|lightpink|lightsalmon|lightseagreen|lightskyblue|lightslategray|lightslategrey|lightsteelblue|lightyellow|lime|limegreen|linen|magenta|maroon|mediumaquamarine|mediumblue|mediumorchid|mediumpurple|mediumseagreen|mediumslateblue|mediumspringgreen|mediumturquoise|mediumvioletred|midnightblue|mintcream|mistyrose|moccasin|navajowhite|navy|oldlace|olive|olivedrab|orange|orangered|orchid|palegoldenrod|palegreen|paleturquoise|palevioletred|papayawhip|peachpuff|peru|pink|plum|powderblue|purple|rebeccapurple|red|rosybrown|royalblue|saddlebrown|salmon|sandybrown|seagreen|seashell|sienna|silver|skyblue|slateblue|slategray|slategrey|snow|springgreen|steelblue|tan|teal|thistle|tomato|turquoise|violet|wheat|white|whitesmoke|yellow|yellowgreen)$/i,
        'Muss ein gültiger CSS-Farbwert sein (z.B. #RRGGBB, rgb(0,0,0), transparent oder eine benannte Farbe)'
    )
];

const validateUrl = [
    regex(/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i, 'Muss eine gültige URL sein (beginnend mit http(s):// oder ftp://)')
];

const borderColorOptions = [
    { id: 'primary', name: 'Primär' },
    { id: 'secondary', name: 'Sekundär' },
    { id: 'custom', name: 'Benutzerdefiniert' },
];

const ListActions = () => {
    const { ids, total } = useListContext();
    //Remove Create button if an item has already been created
    return (
        <TopToolbar>
            {total === 0 && <CreateButton />}
            <ExportButton />
            {/* Add other actions as needed */}
        </TopToolbar>
    );
};

const TestimonialForm = (props) => (
    <SimpleForm>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h4">
                    {props.title}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Grundeinstellungen</Typography>
            </Grid>
            <Grid item xs={12}>
                <BooleanInput fullWidth label="Testimonial ist sichtbar?" source="isVisible" defaultValue={false} />
            </Grid>
        </Grid>

        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Teilnehmer- & Inhaltseinstellungen</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput required fullWidth label="Titel (z.B. Teilnehmername)" source="contestant.name" />
                <TextInput required fullWidth label="Untertitel (z.B. Projekttitel)" source="contestant.subtitle" />
                <TextInput required fullWidth label="Teilnehmer-Asset-URL" source="contestant.image.url" validate={validateUrl} />
                <TextInput required fullWidth label="Alternativtext für das Teilnehmer-Asset" source="contestant.image.altText" />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput required fullWidth label="Testimonial-Text" source="text" multiline rows={9} />
            </Grid>
        </Grid>

        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Autoren- & Sponsoring-Einstellungen</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
                <BooleanInput fullWidth label="Unterschrift ist sichtbar?" source="submitter.isVisible" defaultValue={true} />
                <TextInput fullWidth label="Name des Testimonial-Autors" source="submitter.name" multiline />
                <TextInput fullWidth label="Position des Testimonial-Autors" source="submitter.role" multiline />
            </Grid>
            <Grid item xs={12} md={6}>
                <BooleanInput fullWidth label="Kundenlogo ist sichtbar?" source="customer.isVisible" defaultValue={true} />
                <TextInput fullWidth label="Sponsoring-Text" source="customer.sponsorLabel" defaultValue="Gefördert von" />
                <TextInput fullWidth label="Kundenlogo-Asset-URL" source="customer.logo.url" validate={validateUrl} />
                <TextInput fullWidth label="Alternativtext für das Kundenlogo" source="customer.logo.altText" />
            </Grid>
        </Grid>

        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Präsentationseinstellungen</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
                <SelectInput
                    source="presentation.borderColor"
                    label="Rahmenfarbe"
                    choices={borderColorOptions}
                    fullWidth
                    validate={[required('Farbe ist erforderlich')]}
                    defaultValue="primary"
                    style={{ marginTop: 0 }}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput
                    source="presentation.borderColorCustom"
                    label="Benutzerdefinierte Rahmenfarbe"
                    fullWidth
                    validate={validateCssColor}
                    helperText="Nur aktiv, wenn Rahmenfarbe 'Benutzerdefiniert' gewählt ist."
                />
            </Grid>
        </Grid>
    </SimpleForm>
);

export const TestimonialList = (props) => (
    <List {...props}>
        <Datagrid>
            <BooleanField label="Testimonial sichtbar?" source="isVisible" />
            <FunctionField label="Teilnehmer-Asset" render={record => {
                const imageUrl = record.contestant && record.contestant.image && record.contestant.image.url;
                return (
                    <img
                        src={imageUrl || TestimonialPlaceholderImage}
                        alt={(record.contestant && record.contestant.image && record.contestant.image.altText) || 'Placeholder'}
                        style={{
                            width: "48px",
                            height: "48px", // Set height to match width for perfect circle
                            borderRadius: '50%',
                            objectFit: 'cover',
                            objectPosition: 'center'
                        }}
                    />
                );
            }} />
            <TextField label="Titel (z.B. Teilnehmername)" source="contestant.name" />
            <TextField label="Untertitel (z.B. Projekttitel)" source="contestant.subtitle" />
            <TextField label="Name des Testimonial-Autors" source="submitter.name" />
            <TextField label="Position des Testimonial-Autors" source="submitter.role" />
            <EditButton />
        </Datagrid>
    </List>
);

export const TestimonialCreate = (props) => (
    <Create {...props}>
        <TestimonialForm {...props} title="Neues Testimonial anlegen" />
    </Create>
);

export const TestimonialEdit = (props) => (
    <Edit {...props}>
        <TestimonialForm {...props} title="Testimonial bearbeiten" />
    </Edit>
);